/* @vendored-from tactic:src/modules/greenhouse/lib/layout/rotateGeometry.ts kernel-v1
 * NO EDITAR EN PEST: este archivo se regenera con scripts/sync-greenhouse-kernel.mjs
 * desde el repo de Tactic (fuente de verdad del kernel geométrico). */
/**
 * Rotate 2D points by a given angle around a centroid.
 * Used to axis-align angled lines before computing pixel layout elements.
 */
import type { Point2D } from "../../types/editor";

/** Rotate a single point by `angleDeg` degrees around (cx, cy). */
export function rotatePoint(p: Point2D, angleDeg: number, cx: number, cy: number): Point2D {
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: cos * (p.x - cx) - sin * (p.y - cy) + cx,
    y: sin * (p.x - cx) + cos * (p.y - cy) + cy,
  };
}

/** Rotate an array of points by `angleDeg` around (cx, cy). */
export function rotatePoints(pts: Point2D[], angleDeg: number, cx: number, cy: number): Point2D[] {
  return pts.map(p => rotatePoint(p, angleDeg, cx, cy));
}

/** Compute the centroid of a set of points. */
export function centroid(pts: Point2D[]): Point2D {
  if (pts.length === 0) return { x: 0, y: 0 };
  const sx = pts.reduce((s, p) => s + p.x, 0);
  const sy = pts.reduce((s, p) => s + p.y, 0);
  return { x: sx / pts.length, y: sy / pts.length };
}

/**
 * Shift all points so that the minimum x and y become 0.
 * Returns the shifted points.
 */
export function normalizeToOrigin(pts: Point2D[]): { shifted: Point2D[]; dx: number; dy: number } {
  if (pts.length === 0) return { shifted: [], dx: 0, dy: 0 };
  const minX = Math.min(...pts.map(p => p.x));
  const minY = Math.min(...pts.map(p => p.y));
  return {
    shifted: pts.map(p => ({ x: p.x - minX, y: p.y - minY })),
    dx: -minX,
    dy: -minY,
  };
}
